/**
 * Client for the report-subscription admin routes: the B1 deep-link preset loader
 * plus the B5 subscribe/manage CRUD.
 */
import { adminFetch } from './adminApi';
import type { ReportCriteria } from './reportCriteria';

export interface ReportSubscriptionCriteriaResponse {
  id: string;
  name: string;
  companyId: string;
  /** The stored B0 ReportCriteria payload; the caller re-validates via deserializeReportCriteria. */
  criteria: unknown;
}

export type ReportCadence = 'daily' | 'weekly' | 'monthly';

export interface ReportSubscription {
  id: string;
  owner_user_id: string;
  company_id: string;
  name: string;
  criteria: unknown;
  cadence: ReportCadence;
  send_hour: number;
  day_of_week: number | null;
  day_of_month: number | null;
  timezone: string;
  enabled: boolean;
  last_sent_at: string | null;
  next_due_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReportRecipient {
  id: string;
  user_id: string | null;
  email: string | null;
  unsubscribed_at: string | null;
  created_at: string;
}

/** One target only: an app user XOR a raw email (matches the DB CHECK). */
export type ReportRecipientInput = { userId: string; email?: never } | { email: string; userId?: never };

export interface ReportScheduleInput {
  cadence: ReportCadence;
  sendHour?: number;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
}

export interface CreateReportSubscriptionInput {
  companyId: string;
  name: string;
  criteria: ReportCriteria;
  schedule: ReportScheduleInput;
  timezone?: string;
  recipients?: ReportRecipientInput[];
}

export interface UpdateReportSubscriptionInput {
  name?: string;
  enabled?: boolean;
  criteria?: ReportCriteria;
  cadence?: ReportCadence;
  sendHour?: number;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  timezone?: string;
}

/**
 * Load a subscription's stored criteria for `/reports?preset=<id>`. Rejects with
 * the adminFetch error on 404 (missing OR out of the viewer's company scope —
 * indistinguishable by design, an enumeration guard).
 */
export async function getReportSubscriptionCriteria(id: string): Promise<ReportSubscriptionCriteriaResponse> {
  return adminFetch<ReportSubscriptionCriteriaResponse>(
    `/report-subscriptions/${encodeURIComponent(id)}/criteria`,
  );
}

/** Create a subscription from the current report criteria. */
export async function createReportSubscription(input: CreateReportSubscriptionInput): Promise<{ subscription: ReportSubscription }> {
  return adminFetch('/report-subscriptions', { method: 'POST', body: JSON.stringify(input) });
}

/** The caller's own subscriptions (within their current company scope). */
export async function listReportSubscriptions(): Promise<{ subscriptions: ReportSubscription[] }> {
  return adminFetch('/report-subscriptions');
}

/** One subscription + its recipients. */
export async function getReportSubscription(id: string): Promise<{ subscription: ReportSubscription & { recipients: ReportRecipient[] } }> {
  return adminFetch(`/report-subscriptions/${encodeURIComponent(id)}`);
}

export async function updateReportSubscription(id: string, patch: UpdateReportSubscriptionInput): Promise<{ subscription: ReportSubscription }> {
  return adminFetch(`/report-subscriptions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteReportSubscription(id: string): Promise<void> {
  await adminFetch(`/report-subscriptions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function addReportSubscriptionRecipient(id: string, recipient: ReportRecipientInput): Promise<{ recipient: ReportRecipient }> {
  return adminFetch(`/report-subscriptions/${encodeURIComponent(id)}/recipients`, { method: 'POST', body: JSON.stringify(recipient) });
}

export async function removeReportSubscriptionRecipient(id: string, recipientId: string): Promise<void> {
  await adminFetch(`/report-subscriptions/${encodeURIComponent(id)}/recipients/${encodeURIComponent(recipientId)}`, { method: 'DELETE' });
}
